import React, { useState } from 'react';
import {
  useGetHolidayDaysLimitDefaultsQuery,
  useAddHolidayDaysLimitDefaultMutation,
} from 'store/holidayDaysLimitDefaults/holidayDaysLimitDefaults';
import {
  Dialog,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RowAddButton from 'common/RowAddButton/RowAddButton';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';
import { setErrorCatch, setSuccessStatus } from 'store/status/actions';
import TableBodyContent from 'common/TableBodyContent/TableBodyContent';
import { FormTypes } from 'enums';
import { HolidayDaysLimitDefault } from 'store/holidayDaysLimitDefaults/interfaces';
import { FormPayload } from './DefaultAnnualLeaveForm/interfaces';
import DefaultAnnualLeaveForm from './DefaultAnnualLeaveForm/DefaultAnnualLeaveForm';
import DefaultAnnualRow from './DefaultAnnualRow/DefaultAnnualRow';
import tableHeaders from './tableHeaders';

function DefaultAnnualLeaveList() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {
    data: holidayDayLimits = {
      'hydra:member': [] as HolidayDaysLimitDefault[],
    },
    isLoading: holidayDaysLimitsLoading,
    isError: holidayDaysLimitsError,
  } = useGetHolidayDaysLimitDefaultsQuery({});
  const { t } = useTranslation();

  const [addHolidayLimit, { isLoading: addHolidayLimitLoading }] =
    useAddHolidayDaysLimitDefaultMutation();

  const handleAddAnnualLeave = async (data: FormPayload) => {
    const { days, ...restPayload } = data;
    try {
      await addHolidayLimit({
        days: +data.days,
        ...restPayload,
      }).unwrap();
      dispatch(setSuccessStatus(true));
      setIsAddDialogOpen(false);
    } catch (error) {
      dispatch(setErrorCatch(error));
    }
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.label} align={header.align}>
                  <Typography variant="tableHeader" color="text.secondary">
                    {t(header.label)}
                  </Typography>
                </TableCell>
              ))}
              <TableCell colSpan={tableHeaders.length} />
            </TableRow>
          </TableHead>
          <TableBodyContent
            isError={holidayDaysLimitsError}
            isFetching={holidayDaysLimitsLoading}
            tableHeaders={tableHeaders}
          >
            <RowAddButton
              colSpan={5}
              onClick={() => setIsAddDialogOpen(!isAddDialogOpen)}
            >
              <Typography variant="body2" color="secondary">
                {t('employees.edit_annual_leave_add')}
              </Typography>
            </RowAddButton>
            {holidayDayLimits['hydra:member'].map((holidayDayLimit) => (
              <DefaultAnnualRow
                key={holidayDayLimit.id}
                holidayDayLimit={holidayDayLimit}
              />
            ))}
          </TableBodyContent>
        </Table>
      </TableContainer>
      <Dialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(!isAddDialogOpen)}
        fullWidth
      >
        <DefaultAnnualLeaveForm
          isOpen={isAddDialogOpen}
          setIsOpen={setIsAddDialogOpen}
          onSubmit={handleAddAnnualLeave}
          isSubmitting={addHolidayLimitLoading}
          type={FormTypes.add}
        />
      </Dialog>
    </>
  );
}

export default DefaultAnnualLeaveList;
